import { useState } from 'react';
import { SESSION_STATUSES, SESSION_STATUS_LABEL, type SessionStatus } from '@doit/shared';
import { CalendarPlus, Trash2, Wand2 } from 'lucide-react';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Alert, Button, Card, EmptyState, Spinner } from '../../components/ui';
import { Checkbox } from '../../components/form';
import { Badge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import {
  useDeleteSession,
  useGenerateSessions,
  useSessions,
  useUpdateSession,
} from '../../api/queries';
import type { GenerateResult, GroupDetail, Session } from '../../api/types';
import { formatDayShort } from '../../lib/format';

const STATUS_TONE: Record<SessionStatus, 'neutral' | 'ok' | 'warn' | 'danger'> = {
  SCHEDULED: 'neutral',
  HELD: 'ok',
  POSTPONED: 'warn',
  CANCELLED: 'danger',
};

export function GroupSchedule({ group, canEdit }: { group: GroupDetail; canEdit: boolean }) {
  const toast = useToast();
  const { data: sessions, isLoading } = useSessions({ groupId: group.id, limit: 500 });
  const updateSession = useUpdateSession(group.id);
  const deleteSession = useDeleteSession(group.id);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Session | null>(null);

  async function changeStatus(id: string, status: string) {
    try {
      await updateSession.mutateAsync({ id, data: { status } });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეიცვალა');
    }
  }

  async function handleDelete() {
    if (!toDelete) return;
    try {
      await deleteSession.mutateAsync(toDelete.id);
      toast.success('გაკვეთილი წაიშალა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'წაშლა ვერ მოხერხდა');
    } finally {
      setToDelete(null);
    }
  }

  const list = sessions ?? [];

  return (
    <>
      <Card
        title={`გაკვეთილები (${list.length})`}
        action={
          canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Wand2 className="size-4" />}
              onClick={() => setGenerateOpen(true)}
            >
              განრიგის გენერაცია
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={<CalendarPlus className="size-8" />}
            title="გაკვეთილები ჯერ არ არის"
            description={
              group.weekdays.length === 0
                ? 'ჯერ მიუთითე გაკვეთილის დღეები ჯგუფის რედაქტირებაში.'
                : 'დააჭირე „განრიგის გენერაცია" — თარიღები ავტომატურად დაითვლება.'
            }
          />
        ) : (
          <div className="-mx-5 max-h-[520px] overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 border-y border-line bg-canvas text-left text-[11px] uppercase tracking-wide text-ink-mute">
                <tr>
                  <th className="px-5 py-2.5 font-semibold">#</th>
                  <th className="px-4 py-2.5 font-semibold">თარიღი</th>
                  <th className="px-4 py-2.5 font-semibold">თემა</th>
                  <th className="px-4 py-2.5 font-semibold">სტატუსი</th>
                  <th className="px-4 py-2.5 text-right font-semibold">დასწრება</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} className="border-b border-line last:border-0 hover:bg-canvas/60">
                    <td className="tabular px-5 py-2.5 text-ink-mute">{s.orderNo}</td>
                    <td className="px-4 py-2.5">
                      <span className="block font-medium text-ink">{formatDayShort(s.date)}</span>
                      <span className="tabular block text-[11px] text-ink-mute">{s.startTime}</span>
                    </td>
                    <td className="px-4 py-2.5 text-ink-soft">
                      {s.topic ?? <span className="text-ink-mute">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {canEdit ? (
                        <select
                          value={s.status}
                          onChange={(e) => void changeStatus(s.id, e.target.value)}
                          className="rounded-md border border-line-strong bg-surface px-2 py-1 text-[12px] focus:border-brand-600 focus:outline-none"
                        >
                          {SESSION_STATUSES.map((x) => (
                            <option key={x} value={x}>
                              {SESSION_STATUS_LABEL[x]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Badge tone={STATUS_TONE[s.status]}>{SESSION_STATUS_LABEL[s.status]}</Badge>
                      )}
                    </td>
                    <td className="tabular px-4 py-2.5 text-right text-ink-soft">
                      {s._count.attendance > 0 ? s._count.attendance : '—'}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      {canEdit && s._count.attendance === 0 && (
                        <button
                          onClick={() => setToDelete(s)}
                          aria-label="წაშლა"
                          className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </Card>

      <GenerateModal open={generateOpen} onClose={() => setGenerateOpen(false)} group={group} />

      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
        title="გაკვეთილის წაშლა"
        message={`წაიშლება გაკვეთილი #${toDelete?.orderNo ?? ''} (${formatDayShort(toDelete?.date)}).`}
        confirmLabel="წაშლა"
        danger
        busy={deleteSession.isPending}
      />
    </>
  );
}

// ============================================================
// გენერაცია — ჯერ preview, მერე ჩაწერა
// ============================================================

function GenerateModal({
  open,
  onClose,
  group,
}: {
  open: boolean;
  onClose: () => void;
  group: GroupDetail;
}) {
  const toast = useToast();
  const generate = useGenerateSessions(group.id);

  const [startDate, setStartDate] = useState(group.startDate?.slice(0, 10) ?? '');
  const [count, setCount] = useState(String(group.totalLessons || 40));
  const [skipHolidays, setSkipHolidays] = useState(true);
  const [replaceScheduled, setReplaceScheduled] = useState(false);
  const [preview, setPreview] = useState<GenerateResult | null>(null);

  const payload = {
    startDate: startDate || undefined,
    count: Number(count) || undefined,
    skipHolidays,
    replaceScheduled,
  };

  async function runPreview() {
    try {
      setPreview(await generate.mutateAsync({ ...payload, preview: true }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'გამოთვლა ვერ მოხერხდა');
    }
  }

  async function apply() {
    try {
      const res = await generate.mutateAsync({ ...payload, preview: false });
      toast.success(
        res.created > 0
          ? `დაემატა ${res.created} გაკვეთილი — სულ ${res.total}`
          : (res.warning ?? 'ცვლილება არ მომხდარა'),
      );
      setPreview(null);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'გენერაცია ვერ მოხერხდა');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="განრიგის გენერაცია"
      description={`${group.name} · ${group.weekdays.length ? 'დღეები მითითებულია' : 'დღეები არ არის'}`}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={generate.isPending}>
            გაუქმება
          </Button>
          {preview ? (
            <Button size="sm" onClick={() => void apply()} loading={generate.isPending}>
              დადასტურება
            </Button>
          ) : (
            <Button size="sm" onClick={() => void runPreview()} loading={generate.isPending}>
              გამოთვლა
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="block text-[13px] font-medium text-ink-soft" htmlFor="gen-start">
              დაწყების თარიღი
            </label>
            <input
              id="gen-start"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPreview(null);
              }}
              className="w-full rounded-lg border border-line-strong bg-surface px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-[13px] font-medium text-ink-soft" htmlFor="gen-count">
              ლექციების რაოდენობა
            </label>
            <input
              id="gen-count"
              type="number"
              min={1}
              value={count}
              onChange={(e) => {
                setCount(e.target.value);
                setPreview(null);
              }}
              className="w-full rounded-lg border border-line-strong bg-surface px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
            />
          </div>
        </div>

        <Checkbox
          label="უქმე დღეების გამოტოვება"
          checked={skipHolidays}
          onChange={(v) => {
            setSkipHolidays(v);
            setPreview(null);
          }}
          hint="საქართველოს ოფიციალური დასვენების დღეები"
        />

        <Checkbox
          label="დაგეგმილი გაკვეთილების ხელახლა აგება"
          checked={replaceScheduled}
          onChange={(v) => {
            setReplaceScheduled(v);
            setPreview(null);
          }}
          hint="ჩატარებული და დასწრების მქონე გაკვეთილები არასოდეს იშლება"
        />

        {preview && (
          <div className="space-y-2">
            {preview.warning ? (
              <Alert kind="info">{preview.warning}</Alert>
            ) : (
              <Alert kind="success">
                დაემატება <strong>{preview.created}</strong> გაკვეთილი
                {preview.kept > 0 && ` (არსებული ${preview.kept} რჩება)`} — სულ{' '}
                <strong>{preview.total}</strong>
              </Alert>
            )}

            {preview.dates.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-lg border border-line bg-canvas/50 p-3">
                <div className="tabular flex flex-wrap gap-1.5 text-[11px]">
                  {preview.dates.map((d) => (
                    <span key={d} className="rounded bg-surface px-1.5 py-0.5 text-ink-soft">
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!preview && (
          <p className="rounded-lg bg-canvas px-3 py-2.5 text-[12px] leading-relaxed text-ink-soft">
            ჯერ „გამოთვლა" — ნახავ ზუსტ თარიღებს ჩაწერამდე. გაკვეთილები ჯგუფის დღეებზე
            ({group.weekdays.length ? 'მითითებულია' : 'ჯერ არ არის'}) და დროზე {group.startTime}{' '}
            აეწყობა.
          </p>
        )}
      </div>
    </Modal>
  );
}
